import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Download, MapPin, Search } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errorMessage } from "@/lib/api";
import { fmtDate } from "@/lib/constants";
import { Pill } from "@/components/Pill";

export function OfflineLeads({ products }) {
  const [f, setF] = useState({ product: "", search: "", start: "", end: "" });
  const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v));
  const { data: rows = [], isLoading } = useQuery({ queryKey: ["offline-leads", params], queryFn: () => api.get("/leads/offline", { params }).then((r) => r.data) });
  const exportCsv = async () => {
    try {
      const res = await api.get("/leads/offline/export", { params, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = Object.assign(document.createElement("a"), { href: url, download: `offline-leads-${new Date().toISOString().slice(0, 10)}.csv` });
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Offline leads CSV downloaded");
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };
  return (
    <div className="space-y-4" data-testid="offline-leads">
      <div className="flex flex-wrap items-end gap-2">
        <div className="relative min-w-[200px] flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500" />
          <input className="field-input !py-2.5 !pl-9 !text-sm" placeholder="Search name, phone, email, ticket…" value={f.search} onChange={(e) => setF({ ...f, search: e.target.value })} data-testid="offline-search-input" />
        </div>
        <Select value={f.product || "all"} onValueChange={(v) => setF({ ...f, product: v === "all" ? "" : v })}>
          <SelectTrigger className="h-11 w-[220px] border-2 border-black bg-white text-sm font-semibold" data-testid="offline-product-select"><SelectValue placeholder="Product" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All products</SelectItem>
            {products.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
          </SelectContent>
        </Select>
        <input type="date" className="field-input !w-auto !py-2.5 !text-sm" value={f.start} onChange={(e) => setF({ ...f, start: e.target.value })} data-testid="offline-start-date" />
        <input type="date" className="field-input !w-auto !py-2.5 !text-sm" value={f.end} onChange={(e) => setF({ ...f, end: e.target.value })} data-testid="offline-end-date" />
        <button type="button" className="btn-dark !py-2.5 !text-sm" onClick={exportCsv} data-testid="offline-export-button"><Download className="h-4 w-4" /> CSV</button>
      </div>
      <p className="text-sm font-semibold text-zinc-600" data-testid="offline-count"><MapPin className="mr-1 inline h-4 w-4" />{rows.length} lead{rows.length === 1 ? "" : "s"} interested in Indore offline classes</p>
      {isLoading ? <p className="text-zinc-600">Loading…</p> : (
        <div className="overflow-x-auto rounded-xl border-2 border-black bg-white">
          <table className="w-full text-left text-sm" data-testid="offline-table">
            <thead className="bg-black text-[11px] uppercase tracking-wider text-white">
              <tr><th className="px-4 py-3">Name</th><th className="px-4 py-3">Phone</th><th className="px-4 py-3">Email</th><th className="px-4 py-3">Product</th><th className="px-4 py-3">Date</th><th className="px-4 py-3">Ticket</th></tr>
            </thead>
            <tbody className="divide-y divide-zinc-200">
              {rows.map((r) => (
                <tr key={r.id} data-testid={`offline-row-${r.ticket_id}`}>
                  <td className="px-4 py-3 font-semibold">{r.name}</td>
                  <td className="px-4 py-3 font-mono">{r.phone}</td>
                  <td className="px-4 py-3">{r.email}</td>
                  <td className="px-4 py-3">{r.product}{r.batch ? <span className="block text-xs text-zinc-500">Batch {r.batch}</span> : null}</td>
                  <td className="px-4 py-3 text-zinc-600">{fmtDate(r.created_at)}</td>
                  <td className="px-4 py-3"><span className="font-mono font-bold">{r.ticket_id}</span> <Pill kind="status" value={r.status} className="ml-1" /></td>
                </tr>
              ))}
              {!rows.length && <tr><td colSpan={6} className="px-4 py-8 text-center text-zinc-500">No offline leads match.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
